import React, {useCallback, useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import Button from "@source/components/UI/buttons/button/button";
import TextInput from "@source/components/UI/inputs/textInput/textInput";
import ConsentController from "@source/api/controllers/consentController";
import ReqError from "@source/api/reqError";
import {
  ConsentClinicalIdentityVerificationMethod,
  ConsentDocument,
  ConsentDocumentStatus,
  CONSENT_AGREE_STATEMENT,
  CONSENT_DECLINE_STATEMENT,
} from "@source/api/models/consentModels";
import {
  canUseClinicalPresentationResponse,
  createClinicalPresentationConsentRequest,
  createClinicalPresentationDeclineRequest,
  IdentityVerificationDetail,
  isIdentityEvidenceComplete,
} from "@source/api/models/consentHelpers";
import {IdentityVerificationChoices} from "@source/components/consentDocuments/identityVerificationChoices";
import appStore from "@source/store/appStore";
import {ConsentDocumentPreview} from "@source/components/consentDocuments/consentDocumentPreview";
import {ConsentRequestState} from "@source/components/consentDocuments";
import styles from "./clinicalPresentation.module.scss";

const requestError = (error: unknown) => {
  const status = error instanceof ReqError ? error.status : undefined;
  if (status === 400) return "This action could not be recorded. Check the name, verification method, and form stage, then try again.";
  if (status === 403) return "You do not have permission to present this form.";
  if (status === 404) return "This form is no longer available.";
  if (status === 409) return "This form changed stage. Return to the resident and refresh the form history.";
  return error instanceof Error ? error.message : "Unable to complete this request.";
};

const isTerminalPresentationError = (error: unknown) =>
  error instanceof ReqError
  && (error.status === 403 || error.status === 404 || error.status === 409);

export const ClinicalPresentation = ({nursingHomeId}: {nursingHomeId: string}) => {
  const history = useHistory();
  const {residentId, documentId} = useParams<{residentId: string; documentId: string}>();
  const [document, setDocument] = useState<ConsentDocument>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [verified, setVerified] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [method, setMethod] = useState<ConsentClinicalIdentityVerificationMethod>();
  const [evidence, setEvidence] = useState<IdentityVerificationDetail>({});
  const [previewReady, setPreviewReady] = useState(false);
  const [submitting, setSubmitting] = useState<"consent" | "decline" | null>(null);
  const [result, setResult] = useState<"consent" | "decline" | null>(null);
  const mounted = useRef(true);
  const loadRequest = useRef(0);
  const submission = useRef(false);
  const back = useCallback(
    () => history.push(`/home/residents/${residentId}/consent-documents`),
    [history, residentId],
  );

  const load = useCallback(async () => {
    const requestId = ++loadRequest.current;
    setLoading(true); setError("");
    setDocument(undefined); setVerified(false); setSignerName(""); setMethod(undefined); setEvidence({}); setPreviewReady(false); setResult(null);
    try {
      const response = await ConsentController.getClinicalPresentation(nursingHomeId, documentId);
      if (!mounted.current || requestId !== loadRequest.current) return;
      if (response.resident._id !== residentId) {
        setError("This form belongs to a different resident.");
        return;
      }
      if (!canUseClinicalPresentationResponse(response)) {
        setError("The GP / Clinician is not the active signer for this document. Return to the resident to review its current signing stage.");
        return;
      }
      setDocument(response);
    } catch (requestErrorValue) {
      if (mounted.current && requestId === loadRequest.current) setError(requestError(requestErrorValue));
    } finally {
      if (mounted.current && requestId === loadRequest.current) setLoading(false);
    }
  }, [documentId, nursingHomeId, residentId]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      loadRequest.current += 1;
      window.document.body.classList.remove(styles.presentationBody);
    };
  }, [load]);
  useEffect(() => {
    if ((verified || result) && document) window.document.body.classList.add(styles.presentationBody);
    return () => window.document.body.classList.remove(styles.presentationBody);
  }, [document, result, verified]);
  useEffect(() => {
    if (!document || verified || result) return;
    const header = {
      resident: document.resident,
      title: "Present form to GP / Clinician",
      subtitle: "Additional step · Staff identity verification",
      usePreviousStepNavigation: true,
      onBack: back,
      onCancel: back,
    };
    appStore.setConsentResidentHeader(header);
    return () => appStore.resetConsentResidentHeader(header);
  }, [back, document, result, verified]);

  if (loading) return <ConsentRequestState variant="loading" title="Loading GP / Clinician presentation"/>;
  if (error && !document) return <ConsentRequestState variant="error" title="GP / Clinician presentation unavailable" description={error} onRetry={() => void load()}/>;
  if (!document) return null;
  const trimmedName = signerName.trim();
  const evidenceReady = isIdentityEvidenceComplete(method, evidence);
  const category = document.categorySnapshot?.name || "Legacy form";

  const present = () => {
    if (!trimmedName || !method || !evidenceReady) return;
    setError("");
    setVerified(true);
  };
  const decide = async (decline = false) => {
    if (submission.current || !method) return;
    submission.current = true;
    setSubmitting(decline ? "decline" : "consent"); setError("");
    try {
      const response = decline
        ? await ConsentController.recordClinicalDecline(
          nursingHomeId,
          documentId,
          createClinicalPresentationDeclineRequest(trimmedName, method, evidence),
        )
        : await ConsentController.recordClinicalConsent(
          nursingHomeId,
          documentId,
          createClinicalPresentationConsentRequest(trimmedName, method, evidence),
        );
      if (!mounted.current) return;
      setDocument(response); setResult(decline ? "decline" : "consent");
    } catch (requestErrorValue) {
      if (!mounted.current) return;
      setError(requestError(requestErrorValue));
      if (isTerminalPresentationError(requestErrorValue)) {
        setDocument(undefined);
        setPreviewReady(false);
      }
    } finally {
      submission.current = false;
      if (mounted.current) setSubmitting(null);
    }
  };

  if (!verified) return <div className={styles.start}>
    <section className={styles.identity}>
      <span className={styles.identityStyle}>Staff Identity Verification</span>
      <p className={styles.intro}>Please enter the GP or clinician&apos;s identity details.</p>
      <TextInput id="clinical-signer-name" className={styles.nameField} label="GP / Clinician full name" value={signerName} handleChange={(event) => setSignerName(event.target.value.slice(0, 200))} disabled={Boolean(submitting)} required/>
      <IdentityVerificationChoices signerName={trimmedName} method={method} detail={evidence} disabled={Boolean(submitting)} onMethod={(next) => { setMethod(next); setEvidence({}); }} onDetail={setEvidence}/>
      {error && <p className={styles.error} role="alert">{error}</p>}
      <div className={styles.actions}>
        <Button variant="outlined" onClick={back}>Present later</Button>
        <Button onClick={present} disabled={!trimmedName || !evidenceReady}>Confirm identity</Button>
      </div>
    </section>
  </div>;

  const recordedName = document.clinicalIdentityVerification?.signerName || trimmedName || "GP / Clinician";
  return <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="GP / Clinician form presentation">
    <header className={styles.contextBar}><span>{`${document.nursingHome?.name || "Form"} · ${document.title}`}</span><strong>GP / Clinician on site</strong><span>{recordedName}</span></header>
    <main className={styles.presentationMain}>
      {result ? <section className={styles.result}>
        <h2>{result === "consent" ? "Consent to form recorded" : "Consent to form declined"}</h2>
        <p>{result === "consent"
          ? (document.status === ConsentDocumentStatus.COMPLETED ? "All required form steps are complete." : "The signature was recorded and this form is waiting for the remaining required signers.")
          : "The GP / Clinician declined. This form is now closed."}</p>
        <div className={styles.resultDocument}><strong>{document.title}</strong><span>{category}</span><small>{document.clinicalIdentityVerification?.identityVerificationStatement || "GP / Clinician identity was verified on site."}</small></div>
        <Button className={styles.wideButton} onClick={back}>Return to resident</Button>
      </section> : <section className={styles.presentationPanel}>
        <div className={styles.previewArea}><ConsentDocumentPreview className={styles.presentationPreview} fill documentId={documentId} onReadinessChange={setPreviewReady}/></div>
        <div className={styles.decision}>
          {error && <div className={styles.reviewError} role="alert"><p className={styles.error}>{error}</p><Button size="small" variant="outlined" onClick={back}>Return to resident</Button></div>}
          <div className={styles.decisionActions}>
            <Button className={`${styles.wideButton} ${styles.agreeButton}`} onClick={() => void decide()} disabled={!previewReady || Boolean(submitting)} isLoading={submitting === "consent"}>{CONSENT_AGREE_STATEMENT}</Button>
            <Button className={`${styles.wideButton} ${styles.declineButton}`} onClick={() => void decide(true)} disabled={!previewReady || Boolean(submitting)} isLoading={submitting === "decline"}>{CONSENT_DECLINE_STATEMENT}</Button>
          </div>
        </div>
      </section>}
    </main>
  </div>;
};
